import { FROM_MOBILE_APP_FLAG } from "App/modules/urls";
import { setMembershipTelemetryContext } from "App/modules/membershipTelemetry";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useUser } from "./useEntities/useUser";

/**
 * The subset of the membership flags that `getUserPublic` mirrors onto
 * `publicUser.membershipFlags`.
 *
 * Every field is optional, and every absent field means "off". A missing,
 * empty, or partial payload falls back to legacy behavior. This copies the
 * `loadAdsConfig` / `DEFAULT_ADS_CONFIG` approach, which is the only other
 * runtime-config pattern in the repo.
 *
 * This is a boolean, not the server's mode string. The server runs a
 * three-value mode (`legacy` | `both` | `new`), but every consumer on this
 * side asks the same single question: whether this session reads member
 * documents. The answer is the same for `legacy` and `both`. The string puts
 * a distinction on the wire that no client can act on and that an old bundle
 * can parse wrongly; a client that cannot represent `"both"` cannot mishandle
 * it. The boolean also keeps the free fallback: a missing boolean is falsy,
 * so an old bundle that meets a new payload lands on legacy with no code at
 * all.
 *
 * Warning: `core/features.ts` exports a type with the same name. That one is
 * the producer side, and it lists every field, so the server cannot forget
 * one. This one is the consumer side, and it makes every field optional, so
 * a version mismatch between the client and the payload falls back to
 * `false` instead of a crash on a missing key. `core/features.ts` states the
 * same rule. Import from the side you are on, and do not merge the two types
 * into one.
 */
export type MembershipClientFlags = Partial<{
  membersV2Enabled: boolean;
  disableInMobileWebView: boolean;
}>;

/**
 * The Intercom cohort that turns the new read model on for one user.
 *
 * `syncIntercomCohorts` (`core/intercom.ts`) writes segment membership into
 * `user.cohorts` and strips the `app_cohort_` prefix. So the Intercom segment
 * is `app_cohort_members_v2_new`, and the key seen here is this one.
 *
 * The key is presence-only. The sync derives the map from the attached
 * segments and writes `true` for each one, so `false` is not a value it can
 * produce: a user out of the cohort simply has no key. The check below is
 * therefore `=== true` and not a `typeof === "boolean"` chain. There is no
 * "the cohort forces it off" state to honor. To read one into an absent key
 * is to invent a switch that nobody can turn.
 *
 * That is a claim about another file, so we checked it rather than assumed
 * it. A `false` that reaches `user.cohorts` is the one thing that makes this
 * a silent regression, because the old chain honored it and this check does
 * not:
 *
 *   - `syncIntercomCohorts` builds the map with a single `= true` assignment
 *     per attached segment. There is no other assignment into it.
 *   - The `false` values a few lines below it go into `cohortTraits`, a
 *     separate object handed to Segment's `identify()` for analytics. They
 *     never reach Firestore. Its "Mark removed cohorts as false" comment is
 *     about the analytics sink, not this map. That comment is the one line in
 *     `intercom.ts` most likely to be read as a contradiction of this note.
 *   - The write is `updateEntity({ data: { cohorts } })`, which updates the
 *     top-level `cohorts` field by FieldPath and so replaces the map. Removal
 *     is by absence; a key cannot survive as `false`.
 *   - The only other writer in the repo is
 *     `migrate/submissions/migrateForms/1-provisionUsers.js`, which sets
 *     `cohorts.submissions = true`. That is also true-only, and a different
 *     key.
 *
 * So the removed switch was never connected to anything. And `=== true` is
 * what the two other cohort consumers already use (`isAdEligible` in
 * `getAds.ts`, and discover's `isVisibleForCohorts`); the `typeof` chain was
 * the outlier.
 *
 * One real change remains: under the old chain, a `false` written by hand
 * into a user document forced the flag off. Now it falls through to the
 * server payload instead. That is not a supported switch, and the next sync
 * that finds the map different overwrites it anyway. Use the mode, not a hand
 * edit.
 */
export const MEMBERSHIP_COHORT_KEY = "members_v2_new";

type MembershipFlagsContextValue = {
  /**
   * True when this session reads member documents.
   *
   * This one boolean replaces the six-flag `isEnabled(flag)` / `isAnyEnabled`
   * pair. Here they were always the same question: `isAnyEnabled` gated the
   * open of the member-doc listeners, and each `isEnabled` gated one consumer
   * of them. With one server-side mode they can no longer disagree.
   *
   * Keep the two-step shape at the call sites even though the value is now
   * single: this flag opens the collection-group streams, and the consumers
   * then read them. `Auth/MyMembershipsContext.tsx` depends on that order.
   * Its own comment records the cost of a mistake here: the engine then
   * reports a `resolver_miss` for every space on every boot, and that noise
   * hides the one signal that gates the deletion of the legacy fields.
   */
  membersV2Enabled: boolean;
};

const MembershipFlagsContext = createContext<MembershipFlagsContextValue>({
  membersV2Enabled: false,
});

export const MembershipFlagsContextProvider = ({
  flags,
  children,
}: {
  flags?: MembershipClientFlags;
  children: React.ReactNode;
}) => {
  const { user } = useUser();
  const { fromMobileApp } = useUrlContext();

  /* The app copies `fromMobileApp` into sessionStorage on the first load,
   * and the URL param does not survive the custom-token sign-in, so the
   * durable signal is the sessionStorage copy. The URL half alone goes false
   * in the middle of a session, because every `history.replace` in the app
   * rebuilds the query string. On its own it silently re-enables a flag that
   * the mobile veto turned off. */
  const inMobileWebView =
    Boolean(fromMobileApp) ||
    sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";

  const value = useMemo(() => {
    const cohorts = user?.cohorts || {};

    /* Three levels, most specific first. The global switch is gone: it is
     * now `membersV2Enabled === false` from the server, because the
     * server-side mode folded it in. The fold is strictly stronger than the
     * flag was, since one string, checked in one place, cannot apply to only
     * half the app.
     *
     * A `?membersV2=` URL override was here. We removed it. The Intercom
     * cohort below does the same job for one person, it works on
     * production too, and a flag in a URL travels in a link that a person
     * pastes into a ticket. */
    const membersV2Enabled =
      /* 1. The mobile WebView veto, then the cohort, then the payload. */
      !(inMobileWebView && flags?.disableInMobileWebView) &&
      /* 2. The Intercom cohort, per user. One key replaces six. */
      (cohorts[MEMBERSHIP_COHORT_KEY] === true ||
        /* 3. The server payload. */
        flags?.membersV2Enabled === true);

    return { membersV2Enabled };
  }, [flags, user?.cohorts, inMobileWebView]);

  /* Push the per-session dimensions into the telemetry module. The module is
   * a plain module, not a hook, so this effect pushes the dimensions once,
   * and the events do not read them from a context each time. */
  useEffect(() => {
    setMembershipTelemetryContext({
      inMobileWebView,
      membersV2Enabled: value.membersV2Enabled,
    });
  }, [inMobileWebView, value.membersV2Enabled]);

  return (
    <MembershipFlagsContext.Provider value={value}>
      {children}
    </MembershipFlagsContext.Provider>
  );
};

export const useMembershipFlags = () => useContext(MembershipFlagsContext);
